import { useMemo, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import {
  MobHeader,
  MobTabBar,
  MobOfflineBanner,
  MobPullToRefresh,
  type TabDef,
} from '../../components/mob';
import { UpdatePrompt } from '../../pwa/UpdatePrompt';
import { useSession } from '../../auth/useSession';
import { usePropertyContext } from '../../hooks/api/useAccessibleProperties';
import { useApprovalsCount } from '../../hooks/api/useApprovalsCount';
import { HeaderRefreshButton } from './HeaderRefreshButton';
import { HeaderUserMenu } from './HeaderUserMenu';
import { PropertyPickerSheet } from './PropertyPickerSheet';
import { useCurrentRouteRefresh } from './useCurrentRouteRefresh';
import './AppShell.css';

/*
 * Tab modules — CRS-Mobile-Developer-Guide.md §13.
 * The tab is shown only when the session grants VIEW on the module code.
 */
const TAB_MODULES = {
  today: 'MOB-HOME',
  guests: 'MOB-GUEST',
  meals: 'MOB-MEAL',
  occupancy: 'MOB-OCC',
  shortage: 'MOB-SHORT',
  approvals: 'MOB-APRV',
  mis: 'MOB-MIS',
} as const;

const ICONS = {
  today: (
    <svg viewBox="0 0 24 24">
      <path d="M3 11l9-8 9 8v9a1 1 0 01-1 1h-5v-6h-6v6H4a1 1 0 01-1-1z" />
    </svg>
  ),
  guests: (
    <svg viewBox="0 0 24 24">
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3.5 20c0-3 2.5-5 5.5-5s5.5 2 5.5 5" />
      <circle cx="17" cy="9" r="2.4" />
      <path d="M16 15.2c2.6.2 4.5 2 4.5 4.8" />
    </svg>
  ),
  meals: (
    <svg viewBox="0 0 24 24">
      <path d="M5 3v7M8 3v7M6.5 10v11M16 3c-2 1.5-2.5 5-2.5 7.5 0 1.5 1 2.5 2.5 2.5v8M16 3c2 1.5 2.5 5 2.5 7.5 0 1.5-1 2.5-2.5 2.5" />
    </svg>
  ),
  occupancy: (
    <svg viewBox="0 0 24 24">
      <path d="M4 20V9M10 20V4M16 20v-8M22 20H2" />
    </svg>
  ),
  mis: (
    <svg viewBox="0 0 24 24">
      <path d="M3 3v18h18" />
      <path d="M7 15l4-5 3 3 5-7" />
    </svg>
  ),
  shortage: (
    <svg viewBox="0 0 24 24">
      <path d="M12 3.5L22 20H2z" />
      <path d="M12 10v4M12 17v.01" />
    </svg>
  ),
  approvals: (
    <svg viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="9" />
      <path d="M8.5 12.5l2.4 2.4 4.6-5.3" />
    </svg>
  ),
};

export function AppShell() {
  const { session } = useSession();
  const location = useLocation();
  const [pickerOpen, setPickerOpen] = useState(false);

  // Live property list from crs-api (via BFF), plus selection + switcher.
  const {
    properties,
    selectedProperty,
    selectedPropertyId,
    isLoading: propertiesLoading,
    isError: propertiesError,
    error: propertiesErr,
    refetch: refetchProperties,
    switchTo,
  } = usePropertyContext();

  // Pending approvals count → drives the red badge on the Approvals tab.
  const { data: approvalsCount } = useApprovalsCount();

  // Route-scoped refresh — shared between the header icon and pull-to-refresh.
  const { isRefreshing, refresh } = useCurrentRouteRefresh();

  const visibleModules = useMemo(
    () => new Set(session?.mobileModules ?? []),
    [session],
  );

  const showChevron = properties.length > 1;
  const todayLabel = new Date().toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  const tabs: TabDef[] = useMemo(() => {
    const defs: TabDef[] = [];
    if (visibleModules.has(TAB_MODULES.today)) {
      defs.push({ to: '/today', label: 'Today', icon: ICONS.today });
    }
    if (visibleModules.has(TAB_MODULES.guests)) {
      defs.push({
        to: '/guests/arrivals',
        label: 'Guests',
        icon: ICONS.guests,
        match: (p) => p.startsWith('/guests'),
      });
    }
    if (visibleModules.has(TAB_MODULES.meals)) {
      defs.push({ to: '/meals', label: 'Meals', icon: ICONS.meals });
    }
    if (visibleModules.has(TAB_MODULES.occupancy)) {
      defs.push({
        to: '/occupancy',
        label: 'Occupancy',
        icon: ICONS.occupancy,
      });
    }
    if (visibleModules.has(TAB_MODULES.shortage)) {
      defs.push({
        to: '/shortage',
        label: 'Shortage',
        icon: ICONS.shortage,
      });
    }
    if (visibleModules.has(TAB_MODULES.approvals)) {
      defs.push({
        to: '/approvals',
        label: 'Approvals',
        icon: ICONS.approvals,
        badge: approvalsCount?.pendingCount,
      });
    }
    // Owner Booking Summary. Last in the bar deliberately: it is a management
    // report, not part of anyone's shift, so it must not displace an
    // operational tab for the staff who have both.
    if (visibleModules.has(TAB_MODULES.mis)) {
      defs.push({ to: '/mis', label: 'Summary', icon: ICONS.mis });
    }
    return defs;
  }, [visibleModules, approvalsCount?.pendingCount]);

  const isWizard = location.pathname.startsWith('/quick-booking');
  const showTodaySubtitle = location.pathname.startsWith('/today');

  return (
    <div className="app-shell">
      <MobOfflineBanner />
      {!isWizard ? (
        <MobHeader
          title={selectedProperty?.name ?? (propertiesLoading ? 'Loading…' : 'CRS Mobile')}
          subtitle={showTodaySubtitle ? todayLabel : undefined}
          showPropertyChevron={showChevron}
          onPropertyClick={showChevron ? () => setPickerOpen(true) : undefined}
          right={
            <div className="app-shell__header-right">
              <HeaderRefreshButton />
              <HeaderUserMenu />
            </div>
          }
        />
      ) : null}

      <PropertyPickerSheet
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        properties={properties}
        selectedPropertyId={selectedPropertyId}
        isLoading={propertiesLoading}
        isError={propertiesError}
        errorMessage={
          propertiesErr instanceof Error ? propertiesErr.message : undefined
        }
        onRetry={refetchProperties}
        onSelect={switchTo}
      />

      <main className="app-shell__main">
        <MobPullToRefresh onRefresh={refresh} isRefreshing={isRefreshing}>
          <Outlet />
        </MobPullToRefresh>
      </main>
      {!isWizard && tabs.length > 0 ? <MobTabBar tabs={tabs} /> : null}
      <UpdatePrompt />
    </div>
  );
}
